import React, { useEffect, useContext, useState } from "react";
import { BrowserRouter } from "react-router-dom";
import Header from "./Header";
import * as Sentry from "@sentry/react";
import { getRoleByPriority } from "../../utils/roleProvider";
import {
    getTokenSilent,
    signoutAndRedirect,
} from "../../authentication/authModule";
import { TokenType } from "../../utils/types";
import ServiceWorkerAlert from "../serviceWorker/serviceWorkerAlert";
import Routes from "./Routes";
import {
    RoleContext,
    RoleContextType,
} from "../common/contexts/RoleContextProvider";
import { UserContextProvider } from "../common/contexts/UserContextProvider";
import LinearProgress from "@material-ui/core/LinearProgress";
import Container from "@material-ui/core/Container";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";

function isValidJwt(token: unknown): token is string {
    return (
        typeof token === "string" &&
        token.length > 0 &&
        token.split(".").length === 3
    );
}
// Konverterer base64url til base64 og dekoder payloaden i JWT-tokenet.
function decodeJwtPayload(token: string) {
    const base64Url = token.split(".")[1];
    let base64 = base64Url.replace("-", "+").replace("_", "/");

    while (base64.length % 4 !== 0) {
        base64 += "=";
    }
    const json = atob(base64);
    return JSON.parse(json);
}

const App: React.FC = () => {
    const { setRole, role, setRoleList, roleList } = useContext(
        RoleContext
    ) as RoleContextType;
    const [roleLoadFailed, setRoleLoadFailed] = useState(false);

    useEffect(() => {
        async function getToken() {
            try {
                const token = await getTokenSilent();
                if (!isValidJwt(token)) {
                    setRoleLoadFailed(true);
                    return;
                }
                const tokenModel: TokenType = decodeJwtPayload(token);
                setRoleList(tokenModel.roles ?? []);
                setRole(getRoleByPriority(tokenModel));
            } catch (e) {
                setRoleLoadFailed(true);
                Sentry.captureException(e);
            }
        }

        getToken().catch((e) => {
            setRoleLoadFailed(true);
            Sentry.captureException(e);
        });
    }, [setRole, setRoleList]);

    if (roleLoadFailed) {
        return (
            <Container style={{ marginTop: "2rem", textAlign: "center" }}>
                <Typography variant="body1" style={{ marginBottom: "1rem" }}>
                    Vi klarte ikke å laste inn din sesjon.
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => signoutAndRedirect()}
                >
                    Logg inn på nytt
                </Button>
            </Container>
        );
    }

    if (!role) {
        return <LinearProgress />;
    }

    return (
        <BrowserRouter>
            <UserContextProvider>
                <Header />
                <ServiceWorkerAlert />
                <Routes role={role} roleList={roleList} />
            </UserContextProvider>
        </BrowserRouter>
    );
};

export default App;
